import { useParams, Link, Navigate } from "react-router-dom";
import { Ruler, Gauge, Layers, ShieldCheck, Leaf, CheckCircle2 } from "lucide-react";
import { getCollateral } from "../../lib/collateral";
import { COMPANY, IMAGES } from "../../lib/data";
import { CollateralBar } from "../../components/site/CollateralKit";

const ICONS = [Ruler, Gauge, Layers, ShieldCheck];

export default function ProductDataSheet() {
  const { product } = useParams();
  const c = getCollateral(product);
  if (!c) return <Navigate to="/solutions" replace />;
  const d = c.datasheet;
  const pdf = `/${c.slug}-data-sheet.pdf`;

  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <CollateralBar product={c.slug} label="Technical Data Sheet" pdf={pdf} testid="datasheet" />
      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="collateral-sheet" className="bg-white shadow-2xl print:shadow-none w-[210mm] h-[297mm] p-[13mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
              </div>
              <div className="inline-flex items-center border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.2em]">Technical Data Sheet</div>
              <h1 className="mt-2 font-display font-black tracking-tight text-ink leading-none text-[30px]">{c.name}</h1>
              <p className="mt-1 font-display font-bold text-charcoal text-[12px]">{d.subtitle}</p>
            </div>
            <img src={c.image} alt={c.name} className="w-36 h-28 object-contain bg-white border border-border p-1" />
          </header>

          <div className="grid grid-cols-4 gap-2 mt-3">
            {d.headline.map((h) => (
              <div key={h.l} className="border border-border bg-muted/40 p-2.5 text-center"><div className="font-display font-extrabold text-flame text-[17px] leading-none">{h.v}</div><div className="text-[8px] uppercase tracking-wider text-muted-foreground mt-1">{h.l}</div></div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            {d.groups.map((g, gi) => {
              const Ic = ICONS[gi % ICONS.length];
              return (
                <div key={g.title} className="border border-border">
                  <div className="bg-ink text-white px-3 py-1.5 flex items-center gap-2"><Ic className="h-3.5 w-3.5 text-flame" /><span className="font-display font-bold text-[11px] uppercase tracking-wider">{g.title}</span></div>
                  <dl>
                    {g.rows.map(([k, v], i) => (
                      <div key={k} className={`grid grid-cols-[0.85fr_1.15fr] ${i % 2 ? "bg-muted/40" : "bg-white"}`}>
                        <dt className="px-3 py-1.5 text-muted-foreground text-[9.5px]">{k}</dt>
                        <dd className="px-3 py-1.5 font-mono text-ink text-[9.5px] border-l border-border">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="border border-border">
              <div className="bg-ink text-white px-3 py-1.5 flex items-center gap-2"><Leaf className="h-3.5 w-3.5 text-flame" /><span className="font-display font-bold text-[11px] uppercase tracking-wider">Environmental & Compliance</span></div>
              <ul className="p-3 space-y-1.5">
                {d.env.map((e) => (<li key={e} className="flex items-start gap-2 text-charcoal text-[9.5px] leading-snug"><CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-px" /> {e}</li>))}
              </ul>
            </div>
            <div className="border border-border">
              <div className="bg-ink text-white px-3 py-1.5 font-display font-bold text-[11px] uppercase tracking-wider">Configurations</div>
              {d.skus.map((s, i) => (
                <div key={s[0]} className={`grid grid-cols-[0.9fr_1.3fr_1fr] text-[9px] ${i % 2 ? "bg-muted/40" : "bg-white"}`}>
                  <div className="px-2 py-1.5 font-mono font-semibold text-flame">{s[0]}</div>
                  <div className="px-2 py-1.5 text-charcoal">{s[1]}</div>
                  <div className="px-2 py-1.5 text-charcoal border-l border-border">{s[2]}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-auto pt-3">
            <div className="bg-ink text-white flex items-center justify-between gap-4 px-5 py-3">
              <div><div className="font-display font-extrabold text-[14px]">Need SDS, dosing guidance, or a project spec?</div><div className="text-white/60 text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div></div>
              <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors print:hidden">Request Documents</Link>
            </div>
            <p className="mt-2 text-[8px] text-muted-foreground text-center">{d.note} Specifications representative — confirm certified values for your application. © {new Date().getFullYear()} {COMPANY.legal}.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
